import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

// Import locale files
import es from './locales/es.json';
import en from './locales/en.json';

const locales = { es, en };
const STORAGE_KEY = 'faus_language';

// Create the context
const I18nContext = createContext(null);

/**
 * I18nProvider - Wraps the app to provide localization
 * @param {Object} props
 * @param {string} props.defaultLocale - Default locale (es or en)
 * @param {React.ReactNode} props.children
 */
export function I18nProvider({ defaultLocale = 'es', children }) {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window !== 'undefined') {
      // Check URL hash first (e.g. faus.ai/#es or faus.ai/#en)
      const hash = window.location.hash.replace('#', '');
      if (hash && locales[hash]) {
        localStorage.setItem(STORAGE_KEY, hash);
        return hash;
      }
      // Check localStorage
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && locales[saved]) {
        return saved;
      }
      // Check browser language
      const browserLang = navigator.language?.split('-')[0];
      if (browserLang && locales[browserLang]) {
        return browserLang;
      }
    }
    return defaultLocale;
  });

  // Persist locale changes
  const setLocale = useCallback((newLocale) => {
    if (locales[newLocale]) {
      setLocaleState(newLocale);
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, newLocale);
      }
    }
  }, []);

  // Translation function with interpolation support
  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = locales[locale];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }
    
    // Fallback to Spanish if key not found in current locale
    if (value === undefined && locale !== 'es') {
      value = locales['es'];
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          value = undefined;
          break;
        }
      }
    }
    
    // Return key if no translation found
    if (value === undefined) {
      console.warn(`Missing translation for key: ${key}`);
      return key;
    }
    
    // Handle interpolation: {{variable}}
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (_, name) => {
        return params[name] !== undefined ? params[name] : `{{${name}}}`;
      });
    }
    
    return value;
  }, [locale]);

  // Get all available locales
  const availableLocales = useMemo(() => Object.keys(locales), []);

  // Get locale display names
  const localeNames = useMemo(() => ({
    es: 'Español',
    en: 'English'
  }), []);

  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
    availableLocales,
    localeNames
  }), [locale, setLocale, t, availableLocales, localeNames]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * useI18n - Hook to access localization functions
 * @returns {{ locale: string, setLocale: (locale: string) => void, t: (key: string, params?: object) => string, availableLocales: string[], localeNames: object }}
 */
export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

/**
 * LanguageSwitcher - Component to switch between languages
 */
export function LanguageSwitcher({ className = '' }) {
  const { locale, setLocale, availableLocales, localeNames } = useI18n();
  
  return (
    <select
      value={locale}
      onChange={(e) => setLocale(e.target.value)}
      className={`bg-transparent border border-current rounded px-2 py-1 text-sm cursor-pointer hover:opacity-70 transition-opacity ${className}`}
      aria-label="Select language"
    >
      {availableLocales.map((loc) => (
        <option key={loc} value={loc}>
          {localeNames[loc]}
        </option>
      ))}
    </select>
  );
}

export default I18nProvider;


